// /src/pages/auth/SignupPage.jsx

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { Button, Input } from '../../components/ui';
import { api } from '../../services/api';

export function SignupPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [partialMatch, setPartialMatch] = useState(false);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
    setPartialMatch(false);
  };

  const handlePhoneChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    updateFormData('phone', digits);
  };

  const validateForm = () => {
    if (!formData.first_name.trim()) {
      setError('First name is required');
      return false;
    }
    if (!formData.last_name.trim()) {
      setError('Last name is required');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!formData.phone || formData.phone.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return false;
    }
    return true;
  };

  const handleSubmit = async (proceedAnyway = false) => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const response = await api.createAccountPublic({
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone,
        is_provider: false,
        proceed_with_partial_match: proceedAnyway,
        active_solutions: [5], // ACL Care Pro
      });

      if (response.success) {
        // Navigate to install app page with first name for personalized welcome
        navigate('/install-app', {
          state: {
            firstName: formData.first_name.trim(),
            email: formData.email.trim().toLowerCase(),
          },
          replace: true
        });
      } else if (response.match_type === 'exact') {
        setError('You already have an account. Please sign in.');
      } else if (response.match_type === 'partial' && !proceedAnyway) {
        setPartialMatch(true);
        setError('You might have an existing account under a different email or phone. If you think you have an account, please try signing in or contact support.');
      } else {
        setError(response.message || 'Failed to create account. Please try again.');
      }
    } catch (err) {
      console.error('Signup error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderInfoForm = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Create Your Account</h2>
        <p className="text-gray-600 mt-1">
          Enter your information to get started with your recovery
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Input
          label="First Name"
          type="text"
          value={formData.first_name}
          onChange={(e) => updateFormData('first_name', e.target.value)}
          placeholder="John"
          required
        />
        <Input
          label="Last Name"
          type="text"
          value={formData.last_name}
          onChange={(e) => updateFormData('last_name', e.target.value)}
          placeholder="Doe"
          required
        />
      </div>

      <Input
        label="Email"
        type="email"
        value={formData.email}
        onChange={(e) => updateFormData('email', e.target.value)}
        placeholder="john@example.com"
        required
      />

      <Input
        label="Phone Number"
        type="tel"
        value={formData.phone}
        onChange={handlePhoneChange}
        placeholder="5551234567"
        hint="10 digits, no dashes or spaces"
        required
      />

      {error && (
        <div className={`p-3 rounded-lg flex items-start gap-2 ${partialMatch ? 'bg-yellow-50 border border-yellow-200' : 'bg-red-50 border border-red-200'}`}>
          <AlertCircle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${partialMatch ? 'text-yellow-600' : 'text-red-600'}`} />
          <div>
            <p className={`text-sm ${partialMatch ? 'text-yellow-800' : 'text-red-600'}`}>{error}</p>
            {partialMatch && (
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-sm text-yellow-800 underline hover:no-underline"
                >
                  Sign in instead
                </button>
                <span className="text-yellow-600">•</span>
                <button
                  type="button"
                  onClick={() => handleSubmit(true)}
                  className="text-sm text-yellow-800 underline hover:no-underline"
                >
                  Create new account anyway
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="pt-4">
        <Button
          type="button"
          onClick={() => handleSubmit(false)}
          fullWidth
          loading={loading}
          disabled={loading}
        >
          Create Account
        </Button>
      </div>

      <div className="text-center">
        <Link
          to="/login"
          className="text-sm text-primary hover:text-primary-600"
        >
          Already have an account? Sign in
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center">
      <div className="max-w-md w-full mx-auto px-4">
        <div className="bg-white py-8 px-6 shadow-md rounded-lg">
          {/* Logo and Title */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="flex justify-center mb-4">
                <img
                  src="/images/logo.png"
                  alt="ACL Care"
                  className="h-16 w-auto"
                />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">ACL Care Pro</h1>
          </div>

          {/* Form Content */}
          {renderInfoForm()}
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-gray-500">
          By creating an account, you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  );
}